import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { UserData, getUser } from '../getUserDB';
import type { } from '@mui/x-date-pickers/themeAugmentation';
import type { } from '@mui/x-charts/themeAugmentation';
import type { } from '@mui/x-data-grid-pro/themeAugmentation';
import type { } from '@mui/x-tree-view/themeAugmentation';
import { alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AppNavbar from '../../components/AppNavbar';
import Header from '../../components/Header';
import SideMenu from '../../components/SideMenu';
import AppTheme from '../../shared-theme/AppTheme';
import ActionAreaCard from '../../components/ActionAreaCard';
import PersonIcon from '@mui/icons-material/Person';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import GoogleIcon from '@mui/icons-material/Google';
import { Button, Card, CardContent, Typography, useMediaQuery, useTheme } from '@mui/material';
import { auth } from '../../../contexts/authentication';
import ChangeName from '../../components/dialogs/ChangeName';
import ChangeEmail from '../../components/dialogs/ChangeEmail';
import ChangePhone from '../../components/dialogs/ChangePhone';
import ChangePassword from '../../components/dialogs/ChangePassword';
import SuccessAlert from '../../components/SucessAlert';
import AlertWithDone from '../../components/AlertWithDone';

export default function Profile(props: { disableCustomTheme?: boolean }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentUser = auth.currentUser;
  const [userDB, setUserDB] = useState<UserData>();
  const [googleLogin, setGoogleLogin] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showAlertWithDone, setShowAlertWithDone] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');

  useEffect(() => {
    if (currentUser?.providerData[0].providerId.includes('google')) {
      setGoogleLogin(true);
    }
  }, [currentUser]);

  useEffect(() => {
    const dataUser = async () => {
      const data = await getUser();
      setUserDB(data);
      if (!data.whatsapp) {
        setShowAlertWithDone(true);
        setMessageAlert('Cadastre seu WhatsApp para continuar');
      }
    }
    dataUser();
  }, [messageAlert]);

  useEffect(() => {
    if (showSuccessAlert) {
      setTimeout(() => {
        setShowSuccessAlert(false)
      }, 6000)
    }
  }, [showSuccessAlert])

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme: any) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header />
            <Box sx={{ width: isMobile ? '100%' : '80%' }}>
              <Card>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ mb: 5 }}>
                    <Typography gutterBottom variant={isMobile ? "h6" : "h4"} component="div">
                      Dados do usuário
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Visualize e edite suas informações pessoais
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent={'space-between'}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box>
                          <PersonIcon
                            fontSize={isMobile ? "medium" : "large"}
                            sx={{ color: 'text.secondary' }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Nome do usuário
                          </Typography>
                          <Typography
                            gutterBottom
                            variant="h6"
                            component="div"
                            sx={{ marginBottom: 0 }}
                          >
                            {currentUser?.displayName}
                          </Typography>
                        </Box>
                      </Box>
                      {googleLogin ? (
                        <Box>
                          <GoogleIcon fontSize="large" sx={{ color: 'text.secondary', opacity: 0.3 }} />
                        </Box>
                      ) : (
                        <Box>
                          <Button
                            variant="outlined"
                            onClick={() => setShowNameModal(true)}
                          >
                            Editar
                          </Button>
                        </Box>
                      )}
                      {showNameModal &&
                        <ChangeName
                          showNameModal={showNameModal}
                          setShowNameModal={setShowNameModal}
                          setShowSuccessAlert={setShowSuccessAlert}
                          setMessageAlert={setMessageAlert}
                        />
                      }
                      {showSuccessAlert && (
                        <SuccessAlert message={messageAlert} />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent={'space-between'}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box>
                          <AlternateEmailIcon
                            fontSize={isMobile ? "medium" : "large"}
                            sx={{ color: 'text.secondary' }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Email
                          </Typography>
                          <Typography
                            gutterBottom
                            variant={isMobile ? "body2" : "h6"}
                            component="div"
                            sx={{ marginBottom: 0 }}
                          >
                            {currentUser?.email}
                          </Typography>
                        </Box>
                      </Box>
                      {googleLogin ? (
                        <Box>
                          <GoogleIcon fontSize="large" sx={{ color: 'text.secondary', opacity: 0.3 }} />
                        </Box>
                      ) : (
                        <Box>
                          <Button
                            variant="outlined"
                            onClick={() => setShowEmailModal(true)}
                          >
                            Editar
                          </Button>
                        </Box>
                      )}
                      {showEmailModal &&
                        <ChangeEmail
                          showEmailModal={showEmailModal}
                          setShowEmailModal={setShowEmailModal}
                          setShowAlertWithDone={setShowAlertWithDone}
                        />
                      }
                      {/* !messageAlert garante que o componente nao seja exibido
                      quando ele for chamado para alteração de senha */}
                      {showAlertWithDone && !messageAlert && (
                        <AlertWithDone
                          content={'sendVerificationEmail'}
                          navigateTo={'/signin'}
                          setShowAlertWithDone={setShowAlertWithDone}
                        />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent={'space-between'}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box>
                          <PhoneIcon
                            fontSize={isMobile ? "medium" : "large"}
                            sx={{ color: 'text.secondary' }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Telefone (WhatsApp)
                          </Typography>
                          <Typography
                            gutterBottom
                            variant="h6"
                            component="div"
                            sx={{ marginBottom: 0 }}
                          >
                            {userDB?.whatsapp ? userDB.whatsapp : 'Não cadastrado'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Button
                          variant="outlined"
                          onClick={() => setShowWhatsappModal(true)}
                        >
                          Editar
                        </Button>
                      </Box>
                      {showWhatsappModal &&
                        <ChangePhone
                          showWhatsappModal={showWhatsappModal}
                          setShowWhatsappModal={setShowWhatsappModal}
                          setShowSuccessAlert={setShowSuccessAlert}
                          setMessageAlert={setMessageAlert}
                        />
                      }
                      {showSuccessAlert && (
                        <SuccessAlert message={messageAlert} />
                      )}
                    </Stack>
                    {!googleLogin && (
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent={'space-between'}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Box>
                            <LockIcon
                              fontSize={isMobile ? "medium" : "large"}
                              sx={{ color: 'text.secondary' }}
                            />
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Senha
                            </Typography>
                            <Typography
                              gutterBottom
                              variant="h6"
                              component="div"
                              sx={{ marginBottom: 0 }}
                            >
                              ••••••••
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <Button
                            variant="outlined"
                            onClick={() => setShowPasswordModal(true)}
                          >
                            Alterar
                          </Button>
                        </Box>
                        {showPasswordModal &&
                          <ChangePassword
                            showPasswordModal={showPasswordModal}
                            setShowPasswordModal={setShowPasswordModal}
                            setShowAlertWithDone={setShowAlertWithDone}
                            setMessageAlert={setMessageAlert}
                          />
                        }
                      </Stack>
                    )}
                    {/* messageAlert garante que o componente nao seja exibido
                        quando ele for chamado para alteração de email */}
                    {showAlertWithDone && messageAlert && (
                      <AlertWithDone
                        setShowAlertWithDone={setShowAlertWithDone}
                        alertMessage={messageAlert}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}